import { useId, useState } from 'react';

import { Undo2 } from 'lucide-react';

import { Botao } from '@/componentes/Botao';
import { Campo } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { Janela, Sobreposicao } from '@/componentes/JanelaDeAcao';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { Medida } from '@/componentes/Medida';
import { formatarAteDuasCasas, formatarData, formatarDecimal, interpretarNumero } from '@/lib/formato';

import type { ApontamentoDaApi } from './api';
import { useCadastrosDaOperacao, useCorrigirApontamento, useEstornarApontamento, explicarFalha } from './consultas';
import {
  PISO_DO_MOTIVO,
  montarCorrecao,
  motivoCurto,
  valoresDoTurno,
  type CampoDaCorrecao,
  type ValoresDaCorrecao,
} from './correcao';
import { medidorDo } from './medidor';
import { fraseDoErro } from './texto';

/**
 * ESTORNAR E CORRIGIR O TURNO na mesa (ADR-008, 0086). As duas ações moram
 * nos três pontos da linha (p.18), como no livro do tanque: são exceção, e a
 * ação da linha continua sendo conferir.
 *
 * - **Estornar** é tarefa curta (o motivo): modal ao centro (p.29), com o
 *   botão destrutivo.
 * - **Corrigir** é formulário médio (o turno certo e o motivo, nove campos):
 *   gaveta à direita (p.29). O turno abre como foi apontado; quem corrige muda
 *   só o que está errado. A máquina, o operador e a atividade continuam os do
 *   turno — corrigir a máquina é estornar e apontar na certa.
 *
 * O desenho é o da R06 (p.69): a confirmação pergunta nomeando o alvo, pede o
 * motivo com o contador ("19 de 15") e só liga o vermelho sólido depois do
 * piso; na gaveta, Estornar fica à esquerda em tinta vermelha, e Cancelar e
 * Salvar correção (o primário) à direita. Estornar ali fecha a gaveta e abre a
 * confirmação — nunca dois modais empilhados (p.30).
 *
 * O PISO É 15, e não os 10 da p.69: o motivo do estorno tem 15 caracteres no
 * `check` de `org.estorno` (ADR-008), e o comportamento do livro é decisão do
 * dono. O contador diz o piso que vale ("19 de 15").
 */

const dataDoTurno = (a: ApontamentoDaApi) => `${formatarData(new Date(`${a.dia}T12:00:00`))} · ${a.turno}`;

export function JanelaDeEstorno({ turno, aoFechar }: { readonly turno: ApontamentoDaApi | null; readonly aoFechar: () => void }) {
  const estornar = useEstornarApontamento();
  const [motivo, setMotivo] = useState('');
  const fechar = () => {
    setMotivo('');
    estornar.reset();
    aoFechar();
  };
  const falha = estornar.isError ? explicarFalha(estornar.error) : null;
  const horas = turno === null ? '' : `${formatarDecimal(Number(turno.horasApontadas), 1)} h`;
  return (
    <Janela
      titulo={turno === null ? 'Estornar turno' : `Estornar o turno de ${horas} da ${turno.equipamento}?`}
      aberta={turno !== null}
      aoFechar={fechar}
      largura="p"
      acoes={
        <>
          <Botao nivel="secundario" onClick={fechar}>
            Manter
          </Botao>
          {/* Vermelho sólido só aqui, na decisão final (p.69), e só com o piso do motivo. */}
          <button
            type="button"
            className="botao botao-destrutivo-solido"
            disabled={motivoCurto(motivo) || estornar.isPending}
            onClick={() => {
              if (turno === null) return;
              estornar.mutate({ id: turno.id, motivo: motivo.trim() }, { onSuccess: fechar });
            }}
          >
            {estornar.isPending ? 'Estornando…' : 'Estornar'}
          </button>
        </>
      }
    >
      {turno === null ? null : (
        <div className="flex flex-col gap-4">
          <span aria-hidden="true" className="flex size-12 items-center justify-center rounded-controle bg-erro-fundo">
            <Undo2 className="size-[var(--icone-destaque)] text-erro" />
          </span>
          {/* A recusa no TOPO do formulário (p.22). */}
          {falha ? (
            <p role="alert" className="txt-apoio text-parada">
              {`${fraseDoErro(falha.erro)} ${falha.comoResolver}`}
            </p>
          ) : null}
          <p className="txt-corpo text-pretty text-tinta-secundaria">
            {`${dataDoTurno(turno)}, ${medidorDo(turno.tipoMedidor).noMeio} ${formatarAteDuasCasas(Number(turno.horimetroInicial))} a ${formatarAteDuasCasas(Number(turno.horimetroFinal))} ${medidorDo(turno.tipoMedidor).unidade}. `}
            As <Medida valor={formatarDecimal(Number(turno.horasApontadas), 1)} unidade="h" /> saem das horas do período, com a
            {` leitura de ${medidorDo(turno.tipoMedidor).noMeio} do turno. O lançamento fica visível como estornado.`}
          </p>
          <CampoDoMotivo minimo={PISO_DO_MOTIVO} valor={motivo} aoMudar={setMotivo} exemplo="Turno apontado na máquina errada, era da ESC-002" />
        </div>
      )}
    </Janela>
  );
}

export function GavetaDeCorrecao({
  turno,
  aoFechar,
  aoEstornar,
}: {
  readonly turno: ApontamentoDaApi | null;
  readonly aoFechar: () => void;
  /** Estornar, de dentro da correção: a gaveta fecha e a confirmação abre (p.69). */
  readonly aoEstornar?: (turno: ApontamentoDaApi) => void;
}) {
  const formulario = useId();
  const corrigir = useCorrigirApontamento();
  const fechar = () => {
    corrigir.reset();
    aoFechar();
  };
  return (
    <Sobreposicao
      lugar="direita"
      titulo="Corrigir turno"
      {...(turno ? { descricao: `${turno.equipamento} — ${turno.equipamentoDescricao}` } : {})}
      aberta={turno !== null}
      aoFechar={fechar}
      /* O rodapé fixo da gaveta (p.29): os botões fora do corpo que rola,
         ligados ao formulário pelo atributo `form`. */
      acoes={
        <>
          {/* Estornar à esquerda, em tinta vermelha (p.69). */}
          {aoEstornar && turno ? (
            <Botao
              nivel="destrutivo"
              type="button"
              className="mr-auto"
              onClick={() => {
                corrigir.reset();
                aoEstornar(turno);
              }}
            >
              <Undo2 aria-hidden="true" className="size-[var(--icone-controle)]" />
              Estornar
            </Botao>
          ) : null}
          <Botao nivel="terciario" type="button" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao nivel="primario" type="submit" form={formulario} disabled={corrigir.isPending}>
            {corrigir.isPending ? 'Salvando…' : 'Salvar correção'}
          </Botao>
        </>
      }
    >
      {turno === null ? null : (
        <FormularioDaCorrecao key={turno.id} id={formulario} turno={turno} corrigir={corrigir} aoGravar={fechar} />
      )}
    </Sobreposicao>
  );
}

function FormularioDaCorrecao({
  id,
  turno,
  corrigir,
  aoGravar,
}: {
  readonly id: string;
  readonly turno: ApontamentoDaApi;
  readonly corrigir: ReturnType<typeof useCorrigirApontamento>;
  readonly aoGravar: () => void;
}) {
  const base = useId();
  const cadastros = useCadastrosDaOperacao();
  const [v, setV] = useState<ValoresDaCorrecao>(() => valoresDoTurno(turno));
  const [erro, setErro] = useState<{ campo: CampoDaCorrecao; frase: string } | null>(null);
  const falha = corrigir.isError ? explicarFalha(corrigir.error) : null;

  const mudar = (campo: keyof ValoresDaCorrecao, valor: string) => {
    setV((atual) => ({ ...atual, [campo]: valor }));
    setErro(null);
  };
  const erroDe = (...campos: CampoDaCorrecao[]) => (erro !== null && campos.includes(erro.campo) ? erro.frase : null);

  /* O MOTOR E A SOMA A CADA TECLA (31/10/2026), como no apontamento: quem
     corrige um dígito vê na hora o que o turno passa a dizer. */
  const medidor = medidorDo(turno.tipoMedidor);
  const inicial = interpretarNumero(v.inicial);
  const final = interpretarNumero(v.final);
  const andou = inicial === null || final === null ? null : final - inicial;
  const ajudaDoFinal =
    andou === null ? undefined : `${medidor.tipo === 'odometro' ? 'Rodou no turno' : 'Motor no turno'}: ${formatarDecimal(andou, 1)} ${medidor.unidade}.`;
  const soma = (['producao', 'espera', 'deslocamento', 'manutencao'] as const).reduce(
    (t, c) => t + (v[c].trim() === '' ? 0 : (interpretarNumero(v[c]) ?? 0)),
    0,
  );

  const numero = (campo: 'inicial' | 'final' | 'producao' | 'espera' | 'deslocamento' | 'manutencao', rotulo: string, grupo: CampoDaCorrecao, ajuda?: string) => (
    <Campo id={`${base}-${campo}`} rotulo={rotulo} erro={erroDe(grupo)} {...(ajuda ? { ajuda } : {})}>
      <input
        id={`${base}-${campo}`}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        className="controle w-full text-right tabular-nums"
        value={v[campo]}
        onChange={(e) => {
          mudar(campo, e.target.value);
        }}
      />
    </Campo>
  );

  return (
    <form
      id={id}
      noValidate
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (corrigir.isPending) return;
        const r = montarCorrecao(turno, v);
        if ('campo' in r) {
          setErro(r);
          return;
        }
        corrigir.mutate({ id: turno.id, motivo: r.motivo, apontamento: r.envio }, { onSuccess: aoGravar });
      }}
    >
      {/* A recusa no TOPO do formulário (p.22): a do servidor, e a de nada ter mudado. */}
      {falha ? (
        <p role="alert" className="txt-apoio text-parada">
          {`${fraseDoErro(falha.erro)} ${falha.comoResolver}`}
        </p>
      ) : null}
      {erro?.campo === 'igual' ? (
        <p role="alert" className="txt-apoio text-parada">
          {erro.frase}
        </p>
      ) : null}
      <p className="txt-apoio text-pretty text-tinta-secundaria">
        {`O turno de ${dataDoTurno(turno)} é estornado e o certo entra no lugar, juntos: se o certo for recusado, nada muda. O turno certo fica sem conferir.`}
      </p>

      <h3 className="txt-subtitulo text-tinta">O turno</h3>
      <div className="grid grid-cols-2 gap-4">
        <Campo id={`${base}-dia`} rotulo="Dia" erro={erroDe('dia')}>
          <input id={`${base}-dia`} type="date" className="controle w-full" value={v.dia} onChange={(e) => { mudar('dia', e.target.value); }} />
        </Campo>
        <Campo id={`${base}-turno`} rotulo="Turno" erro={erroDe('turno')}>
          <input id={`${base}-turno`} type="text" autoComplete="off" className="controle w-full" value={v.turno} onChange={(e) => { mudar('turno', e.target.value); }} />
        </Campo>
        {numero('inicial', `${medidor.nome} no início`, 'inicial')}
        {numero('final', `${medidor.nome} no fim`, 'final', ajudaDoFinal)}
      </div>

      <h3 className="txt-subtitulo border-t border-borda-sutil pt-4 text-tinta">Divisão do turno</h3>
      <div className="grid grid-cols-2 gap-4">
        {numero('producao', 'Produção', 'soma')}
        {numero('espera', 'Espera', 'soma')}
        {numero('deslocamento', 'Deslocamento', 'soma')}
        {numero('manutencao', 'Manutenção', 'soma')}
      </div>
      <p className="txt-apoio text-tinta-secundaria">
        Somam <Medida valor={formatarDecimal(soma, 1)} unidade="h" /> do turno.
      </p>
      <Campo id={`${base}-causa`} rotulo="Causa da parada" opcional erro={erroDe('causa')} ajuda="Explica a espera e a manutenção do turno.">
        <ListaDeEscolha
          id={`${base}-causa`}
          rotulo="Causa da parada"
          valor={v.causaId}
          aoMudar={(valor) => {
            mudar('causaId', valor);
          }}
          rotuloVazio="Sem causa"
          idDoErro={erroDe('causa') ? `${base}-causa-erro` : undefined}
          opcoes={(cadastros.data?.motivos ?? []).map((m) => ({
            valor: m.id,
            rotulo: m.nome,
            etiquetas: [{ texto: m.codigo, tom: 'neutro' as const, titulo: 'Código da causa' }],
          }))}
        />
      </Campo>

      <div className="flex flex-col gap-2 border-t border-borda-sutil pt-4">
        <CampoDoMotivo
          minimo={PISO_DO_MOTIVO}
          valor={v.motivo}
          exemplo="Horímetro do fim digitado 15020 em vez de 1520"
          aoMudar={(valor) => {
            mudar('motivo', valor);
          }}
        />
        {erroDe('motivo') ? (
          <p role="alert" className="txt-ajuda text-erro">
            {erroDe('motivo')}
          </p>
        ) : null}
      </div>
    </form>
  );
}
